import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';

// import global app reducers
import { reducer as userReducer } from 'apps/core/user/store';
import { reducer as apiStatusReducer } from 'apps/core/api_status/store';
import { reducer as switcherReducer } from 'apps/core/switcher/store';
import { reducer as authReducer } from 'apps/core/auth/store';
import { reducer as flashNotificationReducer } from 'apps/core/flash_notifications/store';
import { reducer as siteOptionsReducer } from 'apps/core/site_options/store';
import notifications from 'apps/core/notifications/store';

// Custom middleware to pass health_access.accounts to electron via ipc

let electron;

try {
    electron = window.require('electron');
} catch (err) {
    // no electron
}

export const electronHealthAccessMiddleware = store => next => action => {
    let result = next(action);
    if (electron) {
        if (action.type.includes('HEALTH_ACCOUNT')) {
            const accounts = store.getState().healthAccess.accounts;
            electron.ipcRenderer.send('updated-accounts', accounts);
        }
    }
    return result;
};

export const electronHealthSSOMiddleware = store => next => action => {
    let result = next(action);
    if (electron) {
        if (action.type.includes('SET_USER')) {
            const sites = store.getState().user.apps.health_sso || {};
            electron.ipcRenderer.send('updated-sites', sites);
        }
    }
    return result;
};

// Reducers

// merge global app reducers
const globalReducers = {
    switcher: switcherReducer,
    flash_notifications: flashNotificationReducer,
    auth: authReducer,
    user: userReducer,
    [notifications.name]: notifications.reducer,
    api_status: apiStatusReducer,
    site_options: siteOptionsReducer,
};

// Conveniently calling combineReducer with global Reducers will
// give you a merged default state for global reducers
const getInitialState = () => combineReducers(globalReducers)();

// top-level reducer
const crossSliceReducer = (state, action) => {
    switch (action.type) {
        case 'LOGOUT':
            return { ...getInitialState(), auth: state.auth };
        default:
            return state;
    }
};

export const createReducer = (siteReducers = {}) => {
    const slicedReducer = combineReducers({
        ...globalReducers,
        ...siteReducers,
    });

    const reducer = (state, action) => {
        // on merging top-level reducer with regular 'combined'/'sliced' reducers:
        // http://redux.js.org/docs/recipes/reducers/BeyondCombineReducers.html
        return crossSliceReducer(slicedReducer(state, action), action);
    };

    return reducer;
};

export function _createStore(reducer, initialState) {
    const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    return createStore(
        reducer,
        initialState,
        composeEnhancers(
            applyMiddleware(
                thunk,
                electronHealthSSOMiddleware
                //electronHealthAccessMiddleware,  // disable for now, using health_sso
            )
        )
    );
}

export function configureStore(initialState) {
    const reducer = createReducer();
    const store = _createStore(reducer, initialState);

    // add ability to inject reducers from dynamically loaded modules
    // https://redux.js.org/recipes/code-splitting#defining-an-injectreducer-function
    store.asyncReducers = {};
    store.injectReducers = asyncReducers => {
        store.asyncReducers = { ...store.asyncReducers, ...asyncReducers };
        store.replaceReducer(createReducer(store.asyncReducers));
    };

    return store;
}

export const defaultStore = configureStore();
